import { QueryKey } from '@tanstack/react-query'
import isIrrigationDevice from '@weenat/client/dist/core/devices/isIrrigationDevice'
import {
  isModelVirtualWeatherStation,
  isVirtualWeatherStation
} from '@weenat/client/dist/core/devices/isVirtualWeatherStation'
import { useMetricProvider } from '@weenat/client/dist/core/metrics'
import { useToggleIsFavorite } from '@weenat/client/dist/core/plots'
import { getMetricLastUpdateFromSummary } from '@weenat/client/dist/core/summary'
import { UniverseMetric } from '@weenat/client/dist/core/universes'
import { isFrost, isSoilSensorMetric } from '@weenat/client/dist/enums/MetricIds'
import { StationSummary } from '@weenat/client/dist/resources/measurements.type'
import { useIntl } from '@weenat/wintl'
import { useIsAppAccessRestricted } from 'app/src/hooks/useIsAppAccessRestricted'
import useToasts from 'app/src/hooks/useToasts'
import NoEmissionEmptyState from 'app/src/kit/empty-state/NoEmissionEmptyState'
import Icons from 'app/src/kit/Icons'
import Text from 'app/src/kit/Text'
import { useToggleFeature } from 'app/state'
import isNil from 'lodash-es/isNil'
import { MouseEvent, memo, useCallback } from 'react'
import { PlotCardFooter, PlotCardHeader, StyledCard } from './PlotCard'
import PlotListItemMetricSummary from './plotListItem/PlotListItemMetricSummary'

interface StationCardProps {
  focusedMetric: UniverseMetric
  isSelected: boolean
  summariesQueryCacheKey: QueryKey
  summary: StationSummary
  hasNoDataForHorizon: boolean
  isExpertOrg: boolean
  onPress?: () => void
  onClose?: () => void
}

/**
 * @deprecated Use SummaryCard instead. Remove this component when the `marker_redesign` feature flag is removed.
 */
const StationCard: FC<StationCardProps> = ({
  focusedMetric,
  isSelected = false,
  summariesQueryCacheKey,
  summary,
  hasNoDataForHorizon = false,
  isExpertOrg,
  onPress,
  onClose
}) => {
  const { t } = useIntl()
  const { addSuccessToast } = useToasts()

  const [{ marker_redesign, smv }] = useToggleFeature()
  const { name, isFavorite, timezone } = summary.metadata.station

  const StationIcon = isModelVirtualWeatherStation(summary.metadata.station.model) ? (
    <Icons.SingleVirtualSensorFilled $size='md' />
  ) : (
    <Icons.SingleSensorFilled $size='md' />
  )

  const {
    mutation: toggleIsFavorite,
    request: isFavoriteRequest,
    type
  } = useToggleIsFavorite({
    summary,
    summariesQueryCacheKey,
    onSuccess: (data) => {
      addSuccessToast(
        t(
          data.isFavorite
            ? 'actions.add_station_to_favorites_success'
            : 'actions.remove_station_from_favorites_success',
          { stationName: name }
        )
      )
    }
  })

  if (type !== 'isStation') {
    throw new Error('Wrong hook used to add station to favorites')
  }

  const onFavoritePress = useCallback(
    async (e: MouseEvent<Element>) => {
      // Otherwise we open the summary
      e.preventDefault()
      await toggleIsFavorite()
    },
    [toggleIsFavorite]
  )

  const lastUpdate = getMetricLastUpdateFromSummary({
    summary,
    metric: focusedMetric,
    // No need to specify the horizonId here since we only have weather sensors
    horizonId: null
  })

  const {
    isVirtualDevice,
    warningThreshold,
    lastUpdateThresholds,
    name: providerName
  } = useMetricProvider({
    resource: summary.metadata.station,
    metric: focusedMetric,
    isExpertOrg,
    smv
  })

  const { isRestricted, reason } = useIsAppAccessRestricted()

  return (
    <StyledCard $isSelected={isSelected} onPress={onPress}>
      <Flex $flexDirection='column' $height='100%'>
        <PlotCardHeader
          isFavorite={isFavorite}
          isFavoriteRequestLoading={isFavoriteRequest.isPending}
          name={name}
          reverseGeocoding={summary.metadata.station?.reverseGeocoding}
          onFavoritePress={onFavoritePress}
          Icon={StationIcon}
          onClose={onClose}
        />

        {!isNil(focusedMetric) ? (
          // isSoilSensorMetric: HPOT, U_CAPA and T_SOIL_BY_HORIZON are plot-specific metrics — redirect the user instead of showing raw sensor data.
          isSoilSensorMetric(focusedMetric) && isIrrigationDevice(summary.metadata.station) ? (
            <Flex
              $flexDirection='column'
              $alignItems='center'
              $justifyContent='center'
              $flex={1}
              $p='lg'
            >
              <Text $textAlign='center' $color='grayscale.700'>
                {t('models.device.actions.view_sensor_data_on_plot', { capitalize: true })}
              </Text>
            </Flex>
          ) : 'lastUpdatedAt' in summary.metadata && isNil(summary.metadata.lastUpdatedAt) ? (
            <NoEmissionEmptyState
              IconName={
                isVirtualWeatherStation(summary.metadata.station)
                  ? 'SingleVirtualSensorFilled'
                  : 'SingleSensorFilled'
              }
              metrics={[focusedMetric]}
              title={t('weedget.not_emitted_yet.wait_message')}
              description={t('weedget.not_emitted_yet.waiting_on_plot')}
            />
          ) : (
            <Flex
              $flexDirection='column'
              $width='100%'
              $alignItems='center'
              $justifyContent='center'
              $flex={1}
              $px='lg'
              $py='md'
            >
              <PlotListItemMetricSummary
                metric={focusedMetric}
                data={summary.data}
                focusedHorizon={null}
                isRestricted={isRestricted}
                reason={reason}
                isVirtualDevice={
                  smv
                    ? isModelVirtualWeatherStation(summary.metadata.station.model)
                    : isVirtualDevice
                }
                provider={providerName}
                timezone={timezone}
              />
            </Flex>
          )
        ) : null}

        {hasNoDataForHorizon ||
        isSoilSensorMetric(focusedMetric) ||
        isFrost(focusedMetric) ? null : (
          <PlotCardFooter
            timestamp={lastUpdate}
            isVirtualMetric={marker_redesign ? undefined : isVirtualDevice}
            warningThreshold={
              marker_redesign ? lastUpdateThresholds.warningThreshold : warningThreshold
            }
            dangerThreshold={marker_redesign ? lastUpdateThresholds.dangerThreshold : undefined}
            name={providerName}
            timezone={timezone}
          />
        )}
      </Flex>
    </StyledCard>
  )
}

export default memo(StationCard)
